import React, { useEffect, useRef, useState, useCallback } from 'react';
import { initializeThree, loadTextureOntoSphere, updateCameraAndControls, createHotspotRaycaster, handleHotspotHover, disposeAllHotspots, configureControlsForSelectMode } from '../../utils/threeUtils';
import Hotspot from './Hotspot';
import VideoControls from './VideoControls';

const styles = {
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  canvasContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden'
  }
};

function Renderer({ sceneDetails, muted, onLoaded, editorMode = false, onHotspotClick, loading, isSelectMode = false, onRendererClickEvents }) {
  const mountRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const refs = {
    rendererRef: useRef(null),
    sceneRef: useRef(null),
    cameraRef: useRef(null),
    controlsRef: useRef(null),
    sphereRef: useRef(null),
    textureRef: useRef(null)
  };

  // Track last loaded video URL
  const lastVideoUrlRef = useRef();
  const raycasterRef = useRef(null);
  const hoveredHotspotRef = useRef();
  const mouseMoveTimeoutRef = useRef();
  const isMouseInContainerRef = useRef(false);

  // Throttled mouse move handler
  const throttledMouseMove = useCallback((event) => {
    if (!raycasterRef.current || !isMouseInContainerRef.current) return;

    // Clear existing timeout
    if (mouseMoveTimeoutRef.current) {
      clearTimeout(mouseMoveTimeoutRef.current);
    }

    // Throttle to 16ms (60fps)
    mouseMoveTimeoutRef.current = setTimeout(() => {
      raycasterRef.current.updateMousePosition(event, mountRef.current);
      const intersects = raycasterRef.current.checkHotspotIntersections();

      const currentHotspot = intersects.length > 0 ? intersects[0].object : null;

      // Only update if the hotspot has changed
      if (hoveredHotspotRef.current !== currentHotspot) {
        // Reset previously hovered hotspot
        if (hoveredHotspotRef.current) {
          handleHotspotHover(hoveredHotspotRef.current, false);
        }

        // Handle new hover
        if (currentHotspot) {
          handleHotspotHover(currentHotspot, true);
        }

        hoveredHotspotRef.current = currentHotspot;
      }
    }, 16);
  }, []);

  // Handle mouse enter
  const handleMouseEnter = useCallback(() => {
    isMouseInContainerRef.current = true;
  }, []);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    isMouseInContainerRef.current = false;
    
    // Clear any pending mouse move
    if (mouseMoveTimeoutRef.current) {
      clearTimeout(mouseMoveTimeoutRef.current);
      mouseMoveTimeoutRef.current = null;
    }

    // Reset hovered hotspot
    if (hoveredHotspotRef.current) {
      handleHotspotHover(hoveredHotspotRef.current, false);
      hoveredHotspotRef.current = null;
    }
  }, []);

  // Initialize Three.js scene and set global references
  useEffect(() => {
    if (!mountRef.current) return;

    const cleanup = initializeThree(mountRef, refs, sceneDetails?.cameraView);

    // Set global references immediately after initialization
    window._threeDViewerCamera = refs.cameraRef.current;
    window._threeDViewerScene = refs.sceneRef.current;
    window._threeDViewerRenderer = refs.rendererRef.current;
    window._threeDViewerControls = refs.controlsRef.current;

    // Initialize raycaster
    if (refs.cameraRef.current && refs.sceneRef.current) {
      raycasterRef.current = createHotspotRaycaster(refs.cameraRef.current, refs.sceneRef.current);
    }

    const handleClick = (event) => {
      if (!raycasterRef.current) return;

      raycasterRef.current.updateMousePosition(event, mountRef.current);
      const intersects = raycasterRef.current.checkHotspotIntersections();

      // Get click position coordinates
      const rect = mountRef.current.getBoundingClientRect();
      const screenX = event.clientX - rect.left;
      const screenY = event.clientY - rect.top;
      const normalizedX = (screenX / rect.width) * 2 - 1;
      const normalizedY = -(screenY / rect.height) * 2 + 1;

      // Calculate 3D world coordinates using the raycaster utility
      const worldCoordinates = raycasterRef.current ? 
        raycasterRef.current.getWorldCoordinatesFromScreen(normalizedX, normalizedY) : 
        null;

      // Call onRendererClickEvents with all coordinate systems
      if (onRendererClickEvents) {
        onRendererClickEvents({
          screenX: screenX,
          screenY: screenY,
          normalizedX: normalizedX,
          normalizedY: normalizedY,
          clientX: event.clientX,
          clientY: event.clientY,
          worldCoordinates: worldCoordinates
        });
      }

      // Handle hotspot clicks
      if (intersects.length > 0) {
        const hotspot = intersects[0].object;
        if (hotspot.userData.onClick) {
          hotspot.userData.onClick(hotspot.userData.hotspotData || hotspot.userData.inventory);
        }
      }
    };

    mountRef.current.addEventListener('click', handleClick);
    mountRef.current.addEventListener('mousemove', throttledMouseMove);
    mountRef.current.addEventListener('mouseenter', handleMouseEnter);
    mountRef.current.addEventListener('mouseleave', handleMouseLeave);

    // Mark as initialized after everything is set up
    setIsInitialized(true);

    return () => {
      mountRef.current?.removeEventListener('click', handleClick);
      mountRef.current?.removeEventListener('mousemove', throttledMouseMove);
      mountRef.current?.removeEventListener('mouseenter', handleMouseEnter);
      mountRef.current?.removeEventListener('mouseleave', handleMouseLeave);
      
      // Clean up timeouts
      if (mouseMoveTimeoutRef.current) {
        clearTimeout(mouseMoveTimeoutRef.current);
      }
      
      // Reset hover state
      if (hoveredHotspotRef.current) {
        handleHotspotHover(hoveredHotspotRef.current, false);
        hoveredHotspotRef.current = null;
      }
      
      window._threeDViewerCamera = null;
      window._threeDViewerScene = null;
      window._threeDViewerRenderer = null;
      window._threeDViewerControls = null;
      cleanup?.();
    };
  }, [throttledMouseMove, handleMouseEnter, handleMouseLeave]);

  // Enhanced onLoaded callback to set scene ready state
  const handleSceneLoaded = () => {
    setIsSceneReady(true);
    setIsLoading(false);
    if (refs.sphereRef.current) {
      refs.sphereRef.current.visible = true;
    }
    if (onLoaded) onLoaded();
  };

  // Handle scene changes and initial load
  useEffect(() => {
    if (!sceneDetails || !refs.rendererRef.current || !refs.cameraRef.current ||
        !refs.sphereRef.current || !refs.controlsRef.current) return;

    // Hide the sphere immediately when scene changes
    setIsLoading(true);
    if (refs.sphereRef.current) {
      refs.sphereRef.current.visible = false;
    }

    // Reset scene ready state when scene changes
    setIsSceneReady(false);

    if (sceneDetails.inventory.type === 'VIDEO') {
      if (lastVideoUrlRef.current !== sceneDetails.inventory.path) {
        // Cleanup previous video only if the URL is changing
        if (window._threeDViewerVideo) {
          try {
            window._threeDViewerVideo.pause();
            window._threeDViewerVideo.src = '';
            window._threeDViewerVideo.load();
          } catch (e) {}
          window._threeDViewerVideo = null;
        }
        if (refs.textureRef.current) {
          try { refs.textureRef.current.dispose(); } catch (e) {}
          refs.textureRef.current = null;
        }
        lastVideoUrlRef.current = sceneDetails.inventory.path;
        if (window.loadVideoTextureOntoSphere) {
          window.loadVideoTextureOntoSphere(sceneDetails.inventory.path, refs, muted, handleSceneLoaded, sceneDetails.loop, sceneDetails.cameraView);
        }
      } else {
        // Same video URL but properties might have changed, reload to apply new properties
        if (window._threeDViewerVideo) {
          try {
            window._threeDViewerVideo.pause();
            window._threeDViewerVideo.src = '';
            window._threeDViewerVideo.load();
          } catch (e) {}
          window._threeDViewerVideo = null;
        }
        if (refs.textureRef.current) {
          try { refs.textureRef.current.dispose(); } catch (e) {}
          refs.textureRef.current = null;
        }
        if (window.loadVideoTextureOntoSphere) {
          window.loadVideoTextureOntoSphere(sceneDetails.inventory.path, refs, muted, handleSceneLoaded, sceneDetails.loop, sceneDetails.cameraView);
        }
      }
    } else {
      // Cleanup previous video if switching to image
      if (window._threeDViewerVideo) {
        try {
          window._threeDViewerVideo.pause();
          window._threeDViewerVideo.src = '';
          window._threeDViewerVideo.load();
        } catch (e) {}
        window._threeDViewerVideo = null;
      }
      if (refs.textureRef.current) {
        try { refs.textureRef.current.dispose(); } catch (e) {}
        refs.textureRef.current = null;
      }
      lastVideoUrlRef.current = undefined;
      loadTextureOntoSphere(sceneDetails.inventory.path, refs, handleSceneLoaded, sceneDetails.cameraView);
    }
  }, [sceneDetails.uuid, sceneDetails.inventory.type, sceneDetails.inventory.path, sceneDetails.reloadTrigger]);

  // Handle forced reloads when scene properties change
  useEffect(() => {
    
    if (!sceneDetails || !refs.rendererRef.current || !refs.cameraRef.current ||
        !refs.sphereRef.current || !refs.controlsRef.current) return;

    // If reloadTrigger is present, force reload the current scene
    if (sceneDetails.reloadTrigger !== undefined) {
      
      // Hide the sphere during reload
      setIsLoading(true);
      if (refs.sphereRef.current) {
        refs.sphereRef.current.visible = false;
      }
      
      // Reset scene ready state
      setIsSceneReady(false);
       
      if (sceneDetails.inventory.type === 'VIDEO') {
        // Force reload video  
        if (window._threeDViewerVideo) {  // Mi commented
          try {
            window._threeDViewerVideo.pause();
            window._threeDViewerVideo.src = '';
            window._threeDViewerVideo.load();
          } catch (e) {}
          window._threeDViewerVideo = null;
        }
        if (refs.textureRef.current) {
          try { refs.textureRef.current.dispose(); } catch (e) {}
          refs.textureRef.current = null;
        }
        
        // Reload video with current properties
        if (window.loadVideoTextureOntoSphere) {
          window.loadVideoTextureOntoSphere(sceneDetails.inventory.path, refs, muted, handleSceneLoaded, sceneDetails.loop, sceneDetails.cameraView);
        }
      } else {
        // Force reload image
        if (refs.textureRef.current) {
          try { refs.textureRef.current.dispose(); } catch (e) {}
          refs.textureRef.current = null;
        }
        loadTextureOntoSphere(sceneDetails.inventory.path, refs, handleSceneLoaded, sceneDetails.cameraView);
      }
    }
  }, [sceneDetails.reloadTrigger, sceneDetails.uuid, sceneDetails.inventory.type]);

  // Clean up hotspot hover states when scene changes
  useEffect(() => {
    // Reset hover state when scene changes
    if (hoveredHotspotRef.current) {
      handleHotspotHover(hoveredHotspotRef.current, false);
      hoveredHotspotRef.current = null;
    }
    
    // Clear any pending mouse move
    if (mouseMoveTimeoutRef.current) {
      clearTimeout(mouseMoveTimeoutRef.current);
      mouseMoveTimeoutRef.current = null;
    }
    
    // Reset mouse in container state
    isMouseInContainerRef.current = false;

    // Clean up all existing hotspots in the scene to prevent duplicates
    if (refs.sceneRef.current) {
      disposeAllHotspots(refs.sceneRef.current);
    }
  }, [sceneDetails?.uuid, sceneDetails?.inventory?.path]);

  // Update mute state on the existing video element when muted changes
  useEffect(() => {
    if (sceneDetails.inventory.type === 'VIDEO' && window._threeDViewerVideo) {
      window._threeDViewerVideo.muted = muted;
    }
  }, [muted, sceneDetails.inventory.type]);

  // Update loop state on the existing video element when loop changes
  useEffect(() => {
    if (sceneDetails.inventory.type === 'VIDEO' && window._threeDViewerVideo) {
      window._threeDViewerVideo.loop = sceneDetails.loop || false;
    }
  }, [sceneDetails.loop, sceneDetails.inventory.type]);

  // Update controls when editorMode changes
  useEffect(() => {
    if (!refs.controlsRef.current) return;
    
    const controls = refs.controlsRef.current;
    // Store current camera position and target
    const currentPosition = controls.object.position.clone();
    const currentTarget = controls.target.clone();
    
    
    if (!editorMode) {
      // Enable smooth animation in view mode
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = -0.5;
      controls.enableZoom = false;   // Disable OrbitControls zoom - using CSS zoom instead
    } else {
      // Disable smooth animation in editor mode
      controls.enableDamping = false;
      controls.rotateSpeed = -0.5;
      controls.enableZoom = false;   // Disable OrbitControls zoom - using CSS zoom instead
    }

    // Restore camera position and target
    controls.object.position.copy(currentPosition);
    controls.target.copy(currentTarget);
    controls.update();
  }, [editorMode]);

  // Update controls for select mode - right-click drag only
  useEffect(() => {
    if (!refs.controlsRef.current) return;
    configureControlsForSelectMode(refs.controlsRef.current, isSelectMode);
  }, [isSelectMode]);

  // Add debug log for scene type and editor mode
  useEffect(() => {
       
  }, [sceneDetails?.inventory?.type, editorMode]);

  return (
    <div style={styles.container}>
      <div ref={mountRef} style={styles.canvasContainer} />
      {(() => {
        // Debug hotspot rendering - only log once per render cycle
        if (isInitialized && isSceneReady && sceneDetails.hotspots?.map) {
          return sceneDetails.hotspots.map((hotspot, index) => {
            return (
              <Hotspot
                key={`${hotspot.uuid}-${index}`}
                hotspotUuid={hotspot.uuid}
                position={hotspot.position}
                scale={hotspot.scale}
                inventory={hotspot.inventory}
                onHotspotClick={() => onHotspotClick(hotspot.uuid)}
                sceneType={sceneDetails.inventory.type}
                timer={typeof hotspot.timer?.start === 'number' ? hotspot.timer.start : 0}
                type={hotspot.type}
                name={hotspot.name}
                destination={hotspot.destination}
                action={hotspot.action}
              />
            );
          });
        }
        return null;
      })()}
      {editorMode && sceneDetails?.inventory.type === 'VIDEO' && !loading && isInitialized && (
        <VideoControls 
          isEditorMode={editorMode} 
          sceneId={sceneDetails?.uuid} 
          loop={sceneDetails?.loop}
          key={sceneDetails?.uuid} 
        />
      )}
      
    </div>
  );
}

export default Renderer;
